import Link from "@components/Link";
import { styled } from "@linaria/react";
import { Helpers } from "@utils/helpers";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { themeDefault } from "src/theme-revamp";
import CalculatingLoader from "../CalculatingLoader";
import { css } from "@linaria/core";

const NotificationIcon = styled.div<NotificationIconProps>`
  width: ${(props) => (props.width ? props.width : "32px")};
  height: ${(props) => (props.height ? props.height : "32px")};
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    object-fit: contain;
  }
`;

const NotificationLayout = styled.div<{ columnGap?: string }>`
  display: flex;
  column-gap: ${(props) => (props.columnGap ? props.columnGap : "8px")};
  overflow-wrap: anywhere;
  @media screen and (min-width: 768px) {
    column-gap: ${(props) => (props.columnGap ? props.columnGap : "16px")};
    overflow-wrap: unset;
  }
`;

const NotificationDescription = styled.p<{ fontSize?: string }>`
  color: #444444;
  font-size: ${(props) => (props.fontSize ? props.fontSize : "16px")};
  font-weight: 500;
  line-height: 1.4;
`;

const NotificationHeading = styled.div<{ fontSize?: string }>`
  font-size: ${(props) => (props.fontSize ? props.fontSize : "16px")};
  font-weight: 400;
  line-height: 1.4;
  color: #1e1e1e;
  span.purple {
    font-weight: 600;
    color: #883e89;
  }
  span.black {
    font-weight: 600;
    color: #1e1e1e;
  }
`;
const LoaderContainer = styled.div`
  position: absolute;
  top: -10px;
`;
const EmptyLoaderSpace = styled.p`
  display: inline-block;
  width: 50px;
`;

const NotificationContentContainer = styled.div<NotificationContentProps>`
  display: flex;
  flex-direction: column;
  row-gap: ${(props) => (props.rowGap ? props.rowGap : "16px")};
  position: relative;
`;

const NotificationBoxContainer = styled.div<NotificationBoxProps>`
  flex: 1;
  background-color: ${(props) =>
    props.background ? props.background : "#f5f5f5"};
  border-radius: ${(props) => (props.radius ? props.radius : "0px")};
  padding: ${(props) => (props.padding ? props.padding : "16px")};
  margin: ${(props) => (props.margin ? props.margin : "0")};
  @media screen and (min-width: 768px) {
    padding: ${(props) => (props.padding ? props.padding : "24px")};
    border-radius: 16px;
    box-shadow: ${(props) =>
      props.boxShadow
        ? props.boxShadow
        : "0px 4px 16px rgba(204, 204, 204, 0.5)"};
  }
`;

const monthlyBenefitCss = css`
  font-size: 14px;
  font-weight: 600;

  span {
    color: #883e89;
  }
`;

const noteCss = css`
  font-size: 14px;
  font-weight: 400;
  span {
    color: #883e89;
  }
`;

export interface NotificationBoxProps {
  children: JSX.Element;
  className?: string | undefined;
  margin?: string | undefined;
  radius?: string;
  padding?: string;
  background?: string;
  boxShadow?: string;
}

export interface NotificationContentProps {
  children?: React.ReactNode;
  rowGap?: string;
  columnGap?: string;
  amount?: number;
  isCalculating?: boolean;
  iconProps?: NotificationIconProps;
  headerfontSize?: string;
  descFontSize?: string;
  iconFill?: string;
  backgroundFill?: string;
  existingMontlyBenefit?: number;
  validQRClient?: boolean;
  recommendedMonthlyBenefit?: any;
  monthlyBenefit?: any;
}

export interface NotificationIconProps {
  width?: string;
  height?: string;
}

const calculateCashPayment = (amount: number) => {
  if (amount > 600) {
    return amount - 600;
  } else {
    return 0;
  }
};

const NotificationContent = ({
  children,
  rowGap,
  columnGap,
  isCalculating,
  amount = 0,
  iconProps,
  headerfontSize,
  descFontSize,
  iconFill,
  backgroundFill,
  existingMontlyBenefit,
  validQRClient,
  recommendedMonthlyBenefit,
  monthlyBenefit
}: NotificationContentProps) => {
  const [budetCalculatorUrl, setBudgetCalculatorUrl] = useState<string>("");

  useEffect(() => {
    if (!budetCalculatorUrl) {
      let url = get(window, "AVIVA_VARIABLE_CONFIG.budgetCalculatorUrl", "");
      setBudgetCalculatorUrl(url);
    }
  }, [budetCalculatorUrl, setBudgetCalculatorUrl]);

  return (
    <NotificationLayout columnGap={columnGap}>
      <div style={{ flex: 0 }}>
        <NotificationIcon {...iconProps}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill={backgroundFill ? backgroundFill : "none"}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.25 12C22.25 17.6609 17.6609 22.25 12 22.25C6.33908 22.25 1.75 17.6609 1.75 12C1.75 6.33908 6.33908 1.75 12 1.75C17.6609 1.75 22.25 6.33908 22.25 12Z"
              stroke={iconFill ? iconFill : "#434343"}
              strokeWidth="1.5"
            />
            <path
              d="M11.2442 17H12.7562V10H11.2442V17ZM10.9922 7.91401C10.9922 8.50201 11.3982 8.92201 12.0002 8.92201C12.5882 8.92201 13.0082 8.50201 13.0082 7.91401C13.0082 7.32601 12.5882 6.90601 12.0002 6.90601C11.4122 6.90601 10.9922 7.32601 10.9922 7.91401Z"
              fill={iconFill ? iconFill : "#434343"}
            />
          </svg>
        </NotificationIcon>
      </div>
      {!children && (
        <NotificationContentContainer rowGap="8px">
          {isCalculating && (
            <LoaderContainer>
              <CalculatingLoader />
            </LoaderContainer>
          )}
          <NotificationHeading fontSize={headerfontSize}>
            <span className="black"> Cash Payment: </span>{" "}
            {isCalculating ? (
              <EmptyLoaderSpace />
            ) : (
              <span className="purple">
                {Helpers.toCurrency(calculateCashPayment(amount))} per annum{" "}
              </span>
            )}
            (after up to S$600 CPF MediSave deduction)
          </NotificationHeading>
          {/* <NotificationHeading>
            {isCalculating ? <EmptyLoaderSpace /> : <span className='purple'>{Helpers.toCurrency(calculateCashPayment(amount))} </span>}
            cash payment required after
            <span className='black'> S$600.00 CPF</span> Medisave deduction
          </NotificationHeading> */}
          {validQRClient &&
          existingMontlyBenefit &&
          existingMontlyBenefit >= 3000 ? (
            <>
              <div className={monthlyBenefitCss}>
                Your current coverage meets the <span>  recommended{" "}
                S$3,000 </span> monthly amount. You may choose to
                top up now for added protection against rising care costs in
                future.
              </div>
              <div className={noteCss}>
                Minimum add-on amount: <span> S${monthlyBenefit - existingMontlyBenefit}</span>
              </div>
            </>
          ) : (
            <NotificationDescription fontSize={descFontSize}>
              Check if the premium you're paying is affordable based on your{" "}
              <Link
                fontWeight={600}
                underlineColor={"#883E89"}
                theme={themeDefault}
                url={budetCalculatorUrl}
                target="_blank"
                rel="noopener noreferrer"
                text="current income and expenditure."
              ></Link>
            </NotificationDescription>             
          )}
        </NotificationContentContainer>
      )}
      {children && (
        <NotificationContentContainer rowGap={rowGap}>
          {children}
        </NotificationContentContainer>
      )}
    </NotificationLayout>
  );
};

const NotificationBox = ({ ...props }: NotificationBoxProps) => {
  return (
    <NotificationBoxContainer
      className={props.className}
      margin={props.margin}
      radius={props.radius}
      padding={props.padding}
      background={props.background}
      boxShadow={props.boxShadow}
    >
      {props.children}
    </NotificationBoxContainer>
  );
};

export { NotificationBox, NotificationContent };
